import { legacyHttpClient } from "@/infrastructure/http/http.service";
import type {
  LegacyAddToCartRequest,
  LegacyCartResponse,
  LegacyUpdateCartQuantityRequest,
} from "@/types/legacy-cart.types";
import type { SsrClient } from "@/infrastructure/http/ssr-client.service";
import type { HttpClient } from "@/infrastructure/http/http-client.factory";

const legacyBaseUrl = "api/cart";

/**
 * Gets the cart from legacy
 * @param requestClient Browser HttpClient or policy-aware SsrClient.
 * @returns Returns the user's shopping cart
 */
export async function getLegacyCart(
  requestClient: Pick<SsrClient | HttpClient, "get"> = legacyHttpClient,
): Promise<LegacyCartResponse> {
  const response = await requestClient.get<LegacyCartResponse>(
    `${legacyBaseUrl}/getcart/`,
    {
      withCredentials: true,
    },
  );

  return response.data;
}

/**
 * Adds one or more item(s) to the legacy cart
 * @param idiq The idiq to add
 * @param quantity The quantity to add
 * @returns Returns the user's cart after adding the item(s)
 */
export async function addToLegacyCart(idiq: number, quantity: number = 1) {
  if (typeof window === "undefined") {
    throw new Error("addToCart can only be used on the client");
  }

  const request: LegacyAddToCartRequest = {
    idiq,
    quantity,
    getCart: true,
    referringUrl: window.location.href,
    // not sure if these parameters are needed or still used:
    useAbTest: true,
    jitOffer: "",
  };
  const response = await legacyHttpClient.post<LegacyCartResponse>(
    `${legacyBaseUrl}/addtocart/`,
    request,
    {
      withCredentials: true,
    },
  );
  return response.data;
}

/**
 * Updates the quantity for an item in the legacy cart
 * @param idiq the item to update
 * @param quantity the new quantity
 * @returns Returns the user's cart after the update
 */
export async function updateLegacyCartItemQuantity(
  idiq: number,
  quantity: number,
) {
  if (typeof window === "undefined") {
    throw new Error(
      "updateLegacyCartItemQuantity can only be used on the client",
    );
  }

  if (quantity < 0) {
    throw new Error("quantity must be 0 or more");
  }

  const request: LegacyUpdateCartQuantityRequest = { idiq, quantity };
  const response = await legacyHttpClient.post<LegacyCartResponse>(
    `${legacyBaseUrl}/updatequantity/`,
    request,
    {
      withCredentials: true,
    },
  );

  return response.data;
}
